import React from 'react';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Trash2, Plus } from 'lucide-react';
import {
  ConditionGroup,
  Condition,
  LogicOperator,
  FilterType,
  generateConditionId,
  getFilterTypeMetadata,
  getOperatorMetadata,
} from '@/types/filter';
import { ConditionBuilder } from './ConditionBuilder';

interface ConditionGroupBuilderProps {
  group: ConditionGroup;
  onChange: (group: ConditionGroup) => void;
  onRemove: () => void;
}

export function ConditionGroupBuilder({
  group,
  onChange,
  onRemove,
}: ConditionGroupBuilderProps) {
  const handleAddCondition = () => {
    const newCondition: Condition = {
      id: generateConditionId(),
      field: 'from_email', // Default to from_email field
      operator: 'contains',
      value: '',
      caseSensitive: false,
    };

    onChange({
      ...group,
      conditions: [...group.conditions, newCondition],
    });
  };

  const handleUpdateCondition = (index: number, condition: Condition) => {
    const newConditions = [...group.conditions];
    newConditions[index] = condition;
    onChange({
      ...group,
      conditions: newConditions,
    });
  };

  const handleRemoveCondition = (index: number) => {
    onChange({
      ...group,
      conditions: group.conditions.filter((_, i) => i !== index),
    });
  };

  const handleDuplicateCondition = (index: number) => {
    const conditionToDuplicate = group.conditions[index];
    if (!conditionToDuplicate) return;

    // Create a new condition with the same properties but a new ID
    const duplicatedCondition: Condition = {
      ...conditionToDuplicate,
      id: generateConditionId(),
    };

    // Insert the duplicated condition right after the current one
    const newConditions = [...group.conditions];
    newConditions.splice(index + 1, 0, duplicatedCondition);

    onChange({
      ...group,
      conditions: newConditions,
    });

    // Focus the value field of the duplicated condition after DOM update
    const operatorMetadata = getOperatorMetadata(duplicatedCondition.operator);
    if (operatorMetadata.requiresValue) {
      setTimeout(() => {
        const valueField = document.getElementById(`value-${duplicatedCondition.id}`);
        if (valueField) {
          valueField.focus();
        }
      }, 0);
    }
  };

  const handleLogicOperatorChange = (operator: string) => {
    onChange({
      ...group,
      logicOperator: operator as LogicOperator,
    });
  };

  return (
    <div className="p-6 border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg bg-slate-50 dark:bg-gray-900">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <Select
            value={group.logicOperator}
            onValueChange={handleLogicOperatorChange}
          >
            <SelectTrigger className="w-auto min-w-[280px] h-8 bg-white dark:bg-gray-900">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="AND">ALL conditions in this group must be true</SelectItem>
              <SelectItem value="OR">ANY conditions in this group can be true</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={onRemove}
        >
          <Trash2 className="h-4 w-4 mr-1" />
          Remove Group
        </Button>
      </div>

      <div className="space-y-4">
        {group.conditions.map((condition, index) => (
          <React.Fragment key={condition.id}>
            <ConditionBuilder
              condition={condition}
              onChange={(updated) => handleUpdateCondition(index, updated)}
              onRemove={() => handleRemoveCondition(index)}
              onDuplicate={() => handleDuplicateCondition(index)}
            />
            {index < group.conditions.length - 1 && (
              <div className="flex justify-center py-2">
                <span className="text-xs font-normal px-2 py-0.5 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-full text-blue-600">
                  {group.logicOperator === 'AND' ? 'AND' : 'OR'}
                </span>
              </div>
            )}
          </React.Fragment>
        ))}

        {group.conditions.length === 0 && (
          <div className="text-sm text-slate-500 text-center py-4">
            No conditions in this group. Add one to get started.
          </div>
        )}

        <div className="flex justify-center">
          <Button
            type="button"
            variant="secondary"
            size="sm"
            onClick={handleAddCondition}
            className="bg-blue-100"
          >
            <Plus className="h-4 w-4 mr-1" />
            Add Condition to Group
          </Button>
        </div>
      </div>
    </div>
  );
}
